// The LIB patrons console route subtree (console slice 3). Code-based routes
// per DEV-GUIDE-002 §4 — a package-composed subtree hand-writes `.lazy()` +
// `createLazyRoute`. This factory hangs the `/library/patrons` tree off the
// portal's root route so router.tsx stays the single wiring point (composed
// ALONGSIDE the catalogue subtree — neither touches the other).
//
// GATING is per-screen (router.tsx's documented choice): the patrons LAYOUT
// route re-checks lib:patrons.read via useSession/<Show> before it renders any
// child — a defence-in-depth mirror of the API's rbac('lib:patrons.read') guard
// on every patron/group/fine read route (the UI can only HIDE; the gateway is
// the authority, ADR-001 §14.2). Anonymous → /login; signed-in-without-the-grant
// → an explicit denied panel, never a blank Outlet.
import { Outlet, createRoute } from "@tanstack/solid-router";
import { lazyRoute } from "@erp/ui/route-kit";
import { libraryQueries } from "../../index.js";
import { MEMBER_ROLES, MEMBER_STATUSES } from "../../../schemas/index.js";
import type { MemberRole, MemberStatus } from "../../../schemas/index.js";
import { ConsoleLayout } from "@erp/ui/console-layout";
import { panelSearch } from "@erp/ui/side-panel";
import type { PanelSearch } from "@erp/ui/side-panel";
import type { AnyRoute } from "@tanstack/solid-router";

/* ─────────────────────────── AllPatrons URL-as-state ───────────────────────────
 * The keyset roster's filters live in the URL (DEV-GUIDE-002 §4.1), through a
 * hand-written self-healing validator (zod is not a direct portal dep) with
 * the SAME contract as `MemberListQuery` PLUS a client-side `group` param
 * (0015: Member.groupId is real — a group id, or "none" for groupId NULL; the
 * screen narrows the loaded rows because GET /patrons has no groupId filter).
 * A garbage `?role=`/`?status=` heals to the default rather than throwing.
 * Swap for the Zod validators once zod is a portal dep.
 *
 * NO `cursor` (2026-09-22). The roster is an INFINITE stream now: every
 * filter and the search still live in the URL — a shared link restores exactly
 * the roster someone was looking at — but the cursor does not, because it no
 * longer names a page. It names a position inside one accumulating list, which
 * is a different amount of scrolling for every reader. A stale `?cursor=` on
 * an old bookmark simply falls out here, which is the self-healing contract
 * this validator already had. */
export const PATRONS_PAGE_SIZE = 20;

export interface PatronsSearch {
  search?: string;
  role?: MemberRole;
  status?: MemberStatus;
  /** Real-group filter (0015): a patron-group id, or "none" = groupId NULL. Narrows the LOADED rows, client-side. */
  group?: string;
}

function optionalString(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

export function validatePatronsSearch(input: Record<string, unknown>): PatronsSearch {
  const role = MEMBER_ROLES.includes(input.role as MemberRole) ? (input.role as MemberRole) : undefined;
  const status = MEMBER_STATUSES.includes(input.status as MemberStatus) ? (input.status as MemberStatus) : undefined;
  return {
    search: optionalString(input.search),
    role,
    status,
    group: optionalString(input.group),
  };
}

/** Patron-detail deep-link tab (`?tab=fines` from a roster "View fines" action). */
export interface PatronDetailSearch {
  tab?: "fines";
}
export function validatePatronDetailSearch(input: Record<string, unknown>): PatronDetailSearch {
  return { tab: input.tab === "fines" ? "fines" : undefined };
}

/* ───────────────────────── side-panel URL contract ─────────────────────────
 * Every input form and every viewer in this subtree is a URL-addressable
 * SidePanel (`?panel=<name>&panelId=<id>&panelWide=1`). The name set is CLOSED
 * PER ROUTE — `panelSearch` heals anything else to "no panel", so a stale or
 * hand-edited link can never open a panel the screen does not own. The healed
 * params are SPREAD ALONGSIDE each route's existing validator, which is why the
 * patron detail keeps its `?tab=fines` deep link while gaining `?panel=provision`
 * and `?panel=history` (the audit trail for this member — a VIEW, so the URL owns
 * it and a pasted link lands straight on "who changed this record, and when").
 */
export const PATRON_GROUP_PANELS = ["group"] as const;
export const PATRON_CARD_PANELS = ["card"] as const;
export const PATRON_PARENTAL_PANELS = ["profile", "guardian"] as const;
export const PATRON_HOLD_PANELS = ["hold"] as const;
export const PATRON_FINE_PANELS = ["fine", "collect"] as const;
export const PATRON_DETAIL_PANELS = ["provision", "history"] as const;

export function validatePatronDetailPanelSearch(
  input: Record<string, unknown>,
): PatronDetailSearch & PanelSearch {
  return { ...validatePatronDetailSearch(input), ...panelSearch(PATRON_DETAIL_PANELS, input) };
}

/**
 * The patrons shell: gates on lib:patrons.read, then renders the active child.
 * Gating sequence lives in @erp/ui/console-layout; the tab strip is retired in
 * favour of the shell sidebar (modules/lib manifest) + header breadcrumbs.
 */
function PatronsLayout() {
  return (
    <ConsoleLayout
      perm="lib:patrons.read"
      deniedMessage="This account cannot open patrons. Ask an administrator for patron access."
      skeletonId="patrons-skeleton"
      deniedId="patrons-denied"
      unreachableId="patrons-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

/**
 * Build the `/library/patrons` subtree under a portal's root route. Returns the
 * routes to spread into `rootRoute.addChildren([...])` (router.tsx). Screens
 * close their own `.lazy()` loop with `createLazyRoute('/library/patrons/<x>')`;
 * the roster is the subtree INDEX (its id is the parent fullPath) so it lands at
 * `/library/patrons` (the source's AllPatrons.tsx:338 route pattern).
 */
export function makeLibPatronsRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const patronsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "library/patrons",
    component: PatronsLayout,
  });

  // Index = the keyset roster (AllPatrons), read as ONE ACCUMULATING STREAM.
  // URL owns the filters; the loader warms the same cache entry the screen's
  // useInfiniteQuery re-keys (§4.3) — `void prefetchInfiniteQuery`, never an
  // awaited ensure, so the route commits at once and the screen paints its
  // heading + table bones while the first page is on the wire (audit UX-01;
  // the rationale in full sits on lib's student routes). `prefetchInfiniteQuery`
  // warms the FIRST page only (its `pages` default is 1), so the reader's
  // scroll fetches page two, never the loader.
  const rosterRoute = createRoute({
    getParentRoute: () => patronsRoute,
    path: "/",
    validateSearch: validatePatronsSearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({
      search: search.search,
      role: search.role,
      status: search.status,
    }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(
        libraryQueries.patronsInfinite({
          limit: PATRONS_PAGE_SIZE,
          search: deps.search,
          role: deps.role,
          status: deps.status,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../../screens/admin/patrons/AllPatrons.js")));

  // Static `new` is registered before the dynamic `$patronId` (path ranking
  // makes static win regardless; the order documents intent).
  const patronNewRoute = createRoute({
    getParentRoute: () => patronsRoute,
    path: "new",
  }).lazy(lazyRoute(() => import("../../screens/admin/patrons/PatronForm.js")));

  // Static, like `new`, and for the same reason — an invitation is not a patron
  // id. It is a separate screen rather than a panel on the roster because it
  // sends mail: an action with an outside effect deserves a page a librarian
  // arrived at on purpose, not a drawer they can open by fumbling a URL.
  const patronInviteRoute = createRoute({
    getParentRoute: () => patronsRoute,
    path: "invite",
  }).lazy(lazyRoute(() => import("../../screens/admin/patrons/InvitePatron.js")));

  const groupsRoute = createRoute({
    getParentRoute: () => patronsRoute,
    path: "groups",
    validateSearch: (input: Record<string, unknown>) => panelSearch(PATRON_GROUP_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/patrons/PatronGroups.js")));

  const cardsRoute = createRoute({
    getParentRoute: () => patronsRoute,
    path: "cards",
    validateSearch: (input: Record<string, unknown>) => panelSearch(PATRON_CARD_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/patrons/LibraryCards.js")));

  const parentalRoute = createRoute({
    getParentRoute: () => patronsRoute,
    path: "parental",
    validateSearch: (input: Record<string, unknown>) => panelSearch(PATRON_PARENTAL_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/patrons/ParentalControls.js")));

  const holdsRoute = createRoute({
    getParentRoute: () => patronsRoute,
    path: "holds",
    validateSearch: (input: Record<string, unknown>) => panelSearch(PATRON_HOLD_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/patrons/Holds.js")));

  const finesRoute = createRoute({
    getParentRoute: () => patronsRoute,
    path: "fines",
    validateSearch: (input: Record<string, unknown>) => panelSearch(PATRON_FINE_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/patrons/FinesByPatron.js")));

  const patronDetailRoute = createRoute({
    getParentRoute: () => patronsRoute,
    path: "$patronId",
    validateSearch: validatePatronDetailPanelSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/patrons/PatronDetail.js")));

  const patronEditRoute = createRoute({
    getParentRoute: () => patronsRoute,
    path: "$patronId/edit",
  }).lazy(lazyRoute(() => import("../../screens/admin/patrons/PatronForm.js"), (m) => m.EditRoute));

  return [
    patronsRoute.addChildren([
      rosterRoute,
      patronNewRoute,
      patronInviteRoute,
      groupsRoute,
      cardsRoute,
      parentalRoute,
      holdsRoute, finesRoute,
      patronDetailRoute,
      patronEditRoute,
    ]),
  ];
}
